import { useEffect, useState } from "react";
import type { ExpertSummary, MeetingStateResponse } from "../../api";
import { Icon } from "../design/Icon";
import { FinalizeRow, type FinalizeStage } from "./FinalizeRow";

type WireStage = NonNullable<MeetingStateResponse["expert_progress"]>[string];

/** Renders the 2–5 minute "experts are writing up" view.
 *
 *  When the parent passes `expertProgress` (the wire-shape map keyed by
 *  bare expert id), the view shows real per-expert state from the worker.
 *  When absent (older meetings, transient Firestore failure, etc.), the
 *  view falls back to a time-based skeleton matching the design prototype.
 */
export function FinalizingView({
  experts,
  expertProgress,
  onAllDone,
  agent,
}: {
  experts: ExpertSummary[];
  expertProgress?: Record<string, WireStage>;
  /** Called after all experts reach the Done stage — parent should
   *  re-poll the meeting to confirm transition to `finalized`. */
  onAllDone?: () => void;
  /** Resolved per-meeting agent — used to render the archetype name as
   *  a subtitle under each expert's row. Optional for legacy callers. */
  agent?: import("../../api").AgentConfigBody;
}) {
  const hasRealProgress =
    !!expertProgress && Object.keys(expertProgress).length > 0;

  const [t, setT] = useState(0);
  useEffect(() => {
    if (hasRealProgress) return;
    const id = setInterval(() => setT((x) => x + 0.4), 400);
    return () => clearInterval(id);
  }, [hasRealProgress]);

  const fallbackStages: FinalizeStage[] = [
    "queued",
    "transcribing",
    "drafting",
    "writing",
    "done",
  ];

  const rows = experts.map((e, i) => {
    if (hasRealProgress) {
      const wire = expertProgress?.[e.id] ?? "pending";
      return { e, ...stageFromWire(wire) };
    }
    // Time-based fallback (matches the design prototype's skeleton).
    const offset = i * 1.2;
    const p = Math.max(0, Math.min(1, (t - offset) / 14));
    const stageIdx = Math.min(
      fallbackStages.length - 1,
      Math.floor(p * fallbackStages.length),
    );
    return { e, p, stage: fallbackStages[stageIdx] };
  });

  const allDone = rows.every((x) => x.p >= 1);
  useEffect(() => {
    if (allDone && onAllDone) {
      const handle = setTimeout(onAllDone, 800);
      return () => clearTimeout(handle);
    }
  }, [allDone, onAllDone]);

  return (
    <div style={{ padding: "var(--space-5)", overflow: "auto" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <h2
          className="serif"
          style={{
            fontSize: 36,
            fontWeight: 400,
            margin: 0,
            lineHeight: 1.05,
            letterSpacing: "-.01em",
          }}
        >
          Each expert is writing up.
        </h2>
        <p className="dim" style={{ fontSize: 14, maxWidth: 640, marginTop: 10 }}>
          Typically 2–5 minutes. The transcript is already available — you can
          download it now or wait for the full bundle.
        </p>

        <div style={{ marginTop: 28, display: "grid", gap: 14 }}>
          {rows.map(({ e, p, stage }) => {
            const archetypeDisplayName =
              agent && agent.expert_id === e.id
                ? (e.archetypes?.find((a) => a.id === agent.archetype_id)?.display_name ??
                  agent.archetype_id)
                : undefined;
            return (
              <FinalizeRow
                key={e.id}
                expert={e}
                progress={p}
                stage={stage}
                archetypeDisplayName={archetypeDisplayName}
              />
            );
          })}
        </div>

        <BundleRootCard t={t} />
      </div>
    </div>
  );
}

/** Map the api's wire-stage enum to the FinalizeRow's (stage, progress)
 *  pair. The wire only has 4 discrete states (pending/running/done/failed);
 *  we render Running as 50 % progress so the bar isn't either empty or
 *  full mid-stage. */
function stageFromWire(wire: WireStage): { stage: FinalizeStage; p: number } {
  switch (wire) {
    case "pending":
      return { stage: "queued", p: 0 };
    case "running":
      return { stage: "writing", p: 0.5 };
    case "done":
      return { stage: "done", p: 1 };
    case "failed":
      return { stage: "done", p: 1 };
  }
}

function BundleRootCard({ t }: { t: number }) {
  const items = [
    { name: "MEETING.md", desc: "Cross-expert summary", done: t > 4, kind: "md" },
    { name: "transcript.json", desc: "Full transcript", done: true, kind: "json" },
    { name: "chat-log.md", desc: "All [prefix] posts", done: t > 2, kind: "md" },
    {
      name: "frames/",
      desc: `${Math.floor(Math.min(t * 1.2, 11))} frames captured`,
      done: t > 6,
      kind: "folder",
    },
  ];
  return (
    <div
      style={{
        marginTop: 28,
        padding: 16,
        borderRadius: 10,
        border: "1px dashed var(--rule-2)",
        background: "var(--card)",
      }}
    >
      <div className="eyebrow" style={{ marginBottom: 8 }}>
        Bundle root (independent of experts)
      </div>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        {items.map((f) => (
          <div
            key={f.name}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 12.5,
            }}
          >
            <KindIconSmall kind={f.kind} />
            <span className="mono">{f.name}</span>
            <span className="dim">· {f.desc}</span>
            {f.done ? (
              <span
                style={{
                  color: "var(--ok)",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <Icon name="check" size={12} color="var(--ok)" />
              </span>
            ) : (
              <span className="dim mono" style={{ fontSize: 10 }}>
                …
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function KindIconSmall({ kind }: { kind: string }) {
  const map: Record<string, { letter: string; color: string }> = {
    md: { letter: "M", color: "spec" },
    json: { letter: "J", color: "gdpr" },
    folder: { letter: "F", color: "sys" },
  };
  const k = map[kind] ?? map.md;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 18,
        height: 18,
        borderRadius: 3,
        background: `var(--${k.color}-tint)`,
        color: `var(--${k.color}-deep)`,
        fontFamily: "var(--mono)",
        fontSize: 10,
        fontWeight: 700,
        border: `1px solid color-mix(in oklab, var(--${k.color}) 30%, transparent)`,
      }}
    >
      {k.letter}
    </span>
  );
}
